import React, { useEffect, useState } from "react";
import { Loader2, Plug, ShieldCheck, KeyRound, Info } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { refreshGuide } from "@/lib/guide";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { CONFIG, SYSTEM_LABELS, ENV_OPTIONS } from "@/lib/connectionConfig";

const SF_ENV_OPTIONS = [
  { v: "production", l: "Production" },
  { v: "sandbox", l: "Sandbox" },
  { v: "custom", l: "Custom domain (My Domain)" },
];

const SF_CUSTOM_DOMAIN_FIELD = {
  key: "custom_domain", label: "My Domain login URL", type: "text",
  placeholder: "https://yourcompany.my.salesforce.com",
};

const SF_OAUTH_FIELDS = [
  { key: "label", label: "Label", type: "text", placeholder: "e.g. Prod org" },
  { key: "environment", label: "Environment", type: "select", options: SF_ENV_OPTIONS },
  { key: "consumer_key", label: "Consumer Key", type: "text", placeholder: "3MVG…" },
  { key: "consumer_secret", label: "Consumer Secret", type: "password", placeholder: "Connected App consumer secret" },
  { key: "username", label: "Username", hint: "optional hint — shown in UI, not used for auth", type: "text", placeholder: "admin@company.com", optional: true },
];

const SF_CALLBACK_URL = `${API}/connections/salesforce/oauth/callback`;

function buildInitial(system, existingOrg, fields) {
  const cfg = CONFIG[system];
  const base = { label: existingOrg?.label || `${SYSTEM_LABELS[system]} org`, ...cfg.defaults };
  const meta = existingOrg?.meta || existingOrg || {};
  if (meta.data_center) base.data_center = meta.data_center;
  if (meta.environment) base.environment = meta.environment;
  if (meta.instance_url) base.instance_url = meta.instance_url;
  if (meta.account_id) base.account_id = meta.account_id;
  if (meta.username) base.username = meta.username;
  // ensure every active field key exists so inputs are controlled
  (fields || []).forEach((f) => { if (base[f.key] === undefined) base[f.key] = ""; });
  return base;
}

export function OrgEditDialog({ system, open, onOpenChange, existingOrg, mode = "add", onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isSalesforce = system === "salesforce";
  const [sfTab, setSfTab] = useState("oauth"); // oauth | legacy
  const cfg = system ? CONFIG[system] : null;

  let activeFields = isSalesforce && sfTab === "oauth" ? SF_OAUTH_FIELDS : cfg ? cfg.fields : [];
  if (isSalesforce) {
    activeFields = activeFields.map((f) => (f.key === "environment" ? { ...f, options: SF_ENV_OPTIONS } : f));
    if (form.environment === "custom") {
      const i = activeFields.findIndex((f) => f.key === "environment");
      if (i >= 0 && !activeFields.some((f) => f.key === "custom_domain")) {
        activeFields = [...activeFields.slice(0, i + 1), SF_CUSTOM_DOMAIN_FIELD, ...activeFields.slice(i + 1)];
      }
    }
  }

  useEffect(() => {
    if (open && system) {
      const initialTab = isSalesforce
        ? (existingOrg?.method === "credentials" ? "legacy" : "oauth")
        : "oauth";
      setSfTab(initialTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, system]);

  useEffect(() => {
    if (open && system) setForm(buildInitial(system, existingOrg, activeFields));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, system, existingOrg, sfTab]);

  if (!cfg) return null;

  const startOAuth = async () => {
    for (const f of SF_OAUTH_FIELDS) {
      if (f.optional) continue;
      if (!String(form[f.key] || "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    if (form.environment === "custom" && !String(form.custom_domain || "").trim()) {
      toast.error("Enter your My Domain login URL");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/connections/salesforce/oauth/start", {
        label: form.label,
        environment: form.environment || "production",
        custom_domain: form.custom_domain || "",
        consumer_key: form.consumer_key,
        consumer_secret: form.consumer_secret,
        username: form.username || "",
      });
      window.location.href = data.authorize_url; // hand off to Salesforce login/approval
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start Salesforce authorization");
      setSaving(false);
    }
  };

  const save = async () => {
    for (const f of activeFields) {
      if (f.optional) continue;
      if (mode === "edit" && f.type === "password") continue;
      if (!String(form[f.key] || "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.post(`/connections/${system}/org`, form);
      toast.success(cfg.method === "oauth" ? `${SYSTEM_LABELS[system]} connected` : `${SYSTEM_LABELS[system]} org saved`);
      refreshGuide();
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save org");
    } finally {
      setSaving(false);
    }
  };

  const saveLegacySf = async () => {
    for (const key of ["label", "username", "password"]) {
      if (!String(form[key] || "").trim()) {
        toast.error(`${key.charAt(0).toUpperCase() + key.slice(1)} is required`);
        return;
      }
    }
    if (form.environment === "custom" && !String(form.custom_domain || "").trim()) {
      toast.error("Enter your My Domain login URL");
      return;
    }
    setSaving(true);
    try {
      await api.post("/salesforce/orgs/legacy", {
        label: form.label,
        environment: form.environment || "production",
        custom_domain: form.custom_domain || "",
        username: form.username,
        password: form.password,
        security_token: form.security_token || "",
      });
      toast.success("Salesforce org saved");
      refreshGuide();
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save org");
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = isSalesforce
    ? (sfTab === "oauth" ? startOAuth : saveLegacySf)
    : save;

  const startsVowel = ["a", "e", "i", "o", "u"].includes((SYSTEM_LABELS[system] || "")[0]?.toLowerCase());
  const title =
    mode === "edit"
      ? `Edit ${SYSTEM_LABELS[system]} connection`
      : `Add a${startsVowel ? "n" : ""} ${SYSTEM_LABELS[system]} org`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="org-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription>
            {isSalesforce
              ? "Connect a Salesforce org via OAuth 2.0 (recommended) or username + password."
              : cfg.method === "oauth"
              ? "Choose your data center, then authorize with Zoho."
              : mode === "edit"
              ? "Leave secret fields blank to keep the stored values. Credentials are encrypted at rest."
              : "Credentials are encrypted at rest and can be updated anytime."}
          </DialogDescription>
        </DialogHeader>

        {isSalesforce && (
          <div className="flex gap-2" data-testid="sf-auth-tabs">
            <button
              type="button"
              onClick={() => setSfTab("oauth")}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${sfTab === "oauth" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}
              data-testid="sf-tab-oauth"
            >
              OAuth 2.0 (recommended)
            </button>
            <button
              type="button"
              onClick={() => setSfTab("legacy")}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${sfTab === "legacy" ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:text-foreground"}`}
              data-testid="sf-tab-legacy"
            >
              Username + password (legacy)
            </button>
          </div>
        )}

        {isSalesforce && sfTab === "oauth" && (
          <div className="flex gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs leading-relaxed text-muted-foreground" data-testid="sf-oauth-hint">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div>
              Create a Connected App in Salesforce, paste its Consumer Key + Secret here, then approve access on the Salesforce login page.
              Add this exact callback URL and enable scopes <code>api refresh_token offline_access</code> (set Refresh Token Policy to <strong>Infinite</strong> so it never expires):
              <span className="mt-1 block break-all font-mono text-[11px] text-foreground">{SF_CALLBACK_URL}</span>
            </div>
          </div>
        )}

        <div className="space-y-4 py-2">
          {activeFields.map((f) => (
            <div key={f.key} className="space-y-2">
              <Label htmlFor={`fld-${f.key}`}>
                {f.label}
                {f.hint && <span className="ml-1 font-normal text-muted-foreground">({f.hint})</span>}
                {mode === "edit" && f.type === "password" && sfTab !== "oauth" && (
                  <span className="ml-1 font-normal text-muted-foreground">(unchanged if blank)</span>
                )}
              </Label>
              {f.type === "select" ? (
                <Select value={form[f.key] || ""} onValueChange={(v) => setForm((prev) => ({ ...prev, [f.key]: v }))}>
                  <SelectTrigger data-testid={`org-${f.key}-select`}>
                    <SelectValue placeholder={`Select ${f.label.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => (
                      <SelectItem key={o.v} value={o.v} data-testid={`org-${f.key}-${o.v}`}>
                        {o.l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={`fld-${f.key}`}
                  type={f.type === "password" ? "password" : "text"}
                  value={form[f.key] || ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="h-11"
                  data-testid={`org-${f.key}-input`}
                />
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            {isSalesforce && sfTab === "oauth" ? "Encrypted at rest · no passwords stored" : "Encrypted at rest · update anytime"}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={saving} className="gap-2 font-semibold" data-testid="org-save">
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isSalesforce && sfTab === "oauth" ? (
                <KeyRound className="h-4 w-4" />
              ) : (
                cfg.method === "oauth" && <Plug className="h-4 w-4" />
              )}
              {saving
                ? "Redirecting…"
                : isSalesforce && sfTab === "oauth"
                ? "Connect to Salesforce"
                : mode === "edit"
                ? "Save changes"
                : cfg.submit}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
